import React from 'react'
import { Form, Input, Tab } from 'semantic-ui-react'
import { SignatureFormField } from 'elements'
import { SIGNATURE_TYPE } from './report-approval-utils'
import { UploadDropZone } from './report-approval-styles'

const readFileAsDataUrl = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

export const SignatureCapture = ({
    activeTab,
    onTabChange,
    drawnSignature,
    onDrawnChange,
    uploadedSignature,
    onUploadedChange,
    typedName,
    onTypedChange,
    onValidationError,
    maxBytes = 512000,
}) => {
    const handleUpload = async event => {
        const file = event.target.files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) {
            onValidationError?.('Please upload a PNG or JPG image.')
            return
        }
        if (file.size > maxBytes) {
            onValidationError?.('Signature image must be 500 KB or smaller.')
            return
        }
        const dataUrl = await readFileAsDataUrl(file)
        onUploadedChange(String(dataUrl))
        onValidationError?.('')
    }

    const panes = [
        {
            menuItem: 'Draw',
            render: () => (
                <Tab.Pane attached={false}>
                    <SignatureFormField
                        label="Draw your signature"
                        value={drawnSignature}
                        onChange={onDrawnChange}
                    />
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Upload',
            render: () => (
                <Tab.Pane attached={false}>
                    <UploadDropZone>
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={handleUpload}
                        />
                        {uploadedSignature ? (
                            <img
                                src={uploadedSignature}
                                alt="Uploaded signature"
                            />
                        ) : (
                            <>
                                <strong>Drop signature image or browse</strong>
                                <span style={{ fontSize: 12, marginTop: 6 }}>
                                    PNG or JPG · max 500 KB
                                </span>
                            </>
                        )}
                    </UploadDropZone>
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Type name',
            render: () => (
                <Tab.Pane attached={false}>
                    <Form.Field className="signature-input">
                        <label>Type your full name</label>
                        <Input
                            fluid
                            placeholder="Type your full name"
                            value={typedName}
                            onChange={(_, { value }) => onTypedChange(value)}
                        />
                    </Form.Field>
                    <div className="signature-pad">
                        <label className="font-extrabold">Your Signature</label>
                        <div className="signature-preview">
                            {typedName.trim() || 'Signature preview'}
                        </div>
                    </div>
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                        By typing your name and clicking Approve, you affirm
                        this constitutes your electronic signature.
                    </p>
                </Tab.Pane>
            ),
        },
    ]

    return (
        <div className="signature-tabs">
            <Tab
                menu={{ secondary: true, pointing: true }}
                panes={panes}
                activeIndex={activeTab - 1}
                onTabChange={(_, { activeIndex }) =>
                    onTabChange(activeIndex + 1)
                }
            />
        </div>
    )
}

export const getSignatureFromCapture = ({
    activeTab,
    drawnSignature,
    uploadedSignature,
    typedName,
}) => {
    if (activeTab === SIGNATURE_TYPE.Draw) return drawnSignature
    if (activeTab === SIGNATURE_TYPE.Upload) return uploadedSignature
    return typedName
}

export const validateSignatureCapture = ({
    activeTab,
    drawnSignature,
    uploadedSignature,
    typedName,
}) => {
    if (activeTab === SIGNATURE_TYPE.Draw && !drawnSignature) {
        return 'Please draw your signature and click Save before approving.'
    }
    if (activeTab === SIGNATURE_TYPE.Upload && !uploadedSignature) {
        return 'Please upload a signature image.'
    }
    if (activeTab === SIGNATURE_TYPE.Typed && !typedName.trim()) {
        return 'Please type your name.'
    }
    return ''
}
